import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FormFieldOption,
  readLocalizedText,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';

@Component({
  standalone: true,
  selector: 'ain-form-builder-options-editor',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    DragDropModule,
    AinFormFieldComponent,
    InfoHintComponent,
    FormBuilderLocalizedInputComponent
  ],
  template: `
    <section class="options-editor">
      <ain-info-hint
        [titleKey]="'dynamic.builder.options.infoTitle'"
        [descriptionKey]="'dynamic.builder.options.infoDescription'"
        [bulletKeys]="['dynamic.builder.options.infoBullet']" />

      <div class="toolbar">
        <strong>{{ t('dynamic.builder.options.title') }}</strong>
        @if (canEdit()) {
          <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.options.add')" (click)="addOption()"></button>
        }
      </div>

      <div
        class="options-list"
        cdkDropList
        [cdkDropListDisabled]="!canEdit()"
        (cdkDropListDropped)="onOptionDrop($event)">
        @for (option of normalizedOptions(); track $index; let idx = $index) {
          <article class="option-card" cdkDrag [cdkDragDisabled]="!canEdit()" cdkDragLockAxis="y">
            <div class="option-header">
              <button
                type="button"
                class="drag-handle"
                cdkDragHandle
                [disabled]="!canEdit()"
                [attr.aria-label]="t('dynamic.builder.options.reorder')">
                <i class="pi pi-bars"></i>
              </button>
              <span>{{ t('dynamic.builder.options.order') }} {{ idx + 1 }}</span>
              @if (canEdit()) {
                <button
                  pButton
                  type="button"
                  severity="danger"
                  text
                  icon="pi pi-trash"
                  [attr.aria-label]="t('dynamic.builder.options.remove')"
                  (click)="removeOption(idx)"></button>
              }
            </div>

            <ain-form-field [label]="t('dynamic.builder.options.value')">
              <input
                pInputText
                [ngModel]="option.value"
                [disabled]="!canEdit()"
                (ngModelChange)="updateOption(idx, 'value', $event)" />
            </ain-form-field>

            <ain-form-builder-localized-input
              [label]="t('dynamic.builder.options.label')"
              [text]="option.label"
              [disabled]="!canEdit()"
              (textChange)="updateOptionLabel(idx, $event)" />

            <div class="meta-row">
              <label class="enabled-row">
                <p-checkbox
                  [binary]="true"
                  [ngModel]="option.enabled !== false"
                  [disabled]="!canEdit()"
                  (ngModelChange)="updateOption(idx, 'enabled', $event)" />
                <span>{{ t('dynamic.builder.options.enabled') }}</span>
              </label>
            </div>

            <div class="meta-row">
              <ain-form-field [label]="t('dynamic.builder.options.color')">
                <input
                  pInputText
                  [ngModel]="option.color ?? ''"
                  [disabled]="!canEdit()"
                  [placeholder]="t('dynamic.builder.options.colorPlaceholder')"
                  (ngModelChange)="updateOption(idx, 'color', $event)" />
              </ain-form-field>
              <ain-form-field [label]="t('dynamic.builder.options.icon')">
                <input
                  pInputText
                  [ngModel]="option.icon ?? ''"
                  [disabled]="!canEdit()"
                  [placeholder]="t('dynamic.builder.options.iconPlaceholder')"
                  (ngModelChange)="updateOption(idx, 'icon', $event)" />
              </ain-form-field>
            </div>
          </article>
        }
      </div>
    </section>
  `,
  styles: [`
    .options-editor { display: grid; gap: 12px; }
    .toolbar { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .options-list { display: grid; gap: 10px; }
    .option-card {
      display: grid;
      gap: 8px;
      padding: 10px;
      border: 1px dashed #cbd5e1;
      border-radius: 8px;
      background: #f8fafc;
    }
    .option-header {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: space-between;
    }
    .drag-handle {
      border: none;
      background: transparent;
      cursor: grab;
      color: #64748b;
      padding: 0;
    }
    .meta-row { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); }
    .enabled-row { display: flex; align-items: center; gap: 8px; }
  `]
})
export class FormBuilderOptionsEditorComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly options = input<FormFieldOption[]>([]);
  readonly canEdit = input(true);

  readonly optionsChange = output<FormFieldOption[]>();

  readonly normalizedOptions = computed(() => this.options() ?? []);

  addOption(): void {
    const next = [...this.normalizedOptions()];
    next.push({
      value: `option_${next.length + 1}`,
      label: writeLocalizedText({}, this.editCultureService.editCulture(), `Option ${next.length + 1}`),
      order: next.length + 1,
      enabled: true
    });
    this.emitWithOrder(next);
  }

  removeOption(index: number): void {
    const next = this.normalizedOptions().filter((_, idx) => idx !== index);
    this.emitWithOrder(next);
  }

  updateOption(index: number, prop: 'value' | 'enabled' | 'color' | 'icon', value: string | boolean): void {
    const next = [...this.normalizedOptions()];
    const current = next[index];
    if (!current) {
      return;
    }

    if (prop === 'value') {
      next[index] = { ...current, value: String(value) };
    } else if (prop === 'enabled') {
      next[index] = { ...current, enabled: !!value };
    } else if (prop === 'color') {
      next[index] = { ...current, color: String(value).trim() || undefined };
    } else {
      next[index] = { ...current, icon: String(value).trim() || undefined };
    }

    this.emitWithOrder(next);
  }

  updateOptionLabel(index: number, label: FormFieldOption['label']): void {
    const next = [...this.normalizedOptions()];
    const current = next[index];
    if (!current) {
      return;
    }
    next[index] = { ...current, label };
    this.emitWithOrder(next);
  }

  onOptionDrop(event: CdkDragDrop<FormFieldOption[]>): void {
    const next = [...this.normalizedOptions()];
    moveItemInArray(next, event.previousIndex, event.currentIndex);
    this.emitWithOrder(next);
  }

  private emitWithOrder(options: FormFieldOption[]): void {
    this.optionsChange.emit(
      options.map((option, index) => ({
        ...option,
        order: index + 1
      }))
    );
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
